import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title,
  Group,
  Button,
  Text,
  Loader,
  ActionIcon,
  SegmentedControl,
  em,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { PageContainer } from '../../../components/ui/PageContainer';
import { BackButton } from '../../../components/ui/BackButton';
import { LoadFailed } from '../../../components/ui/LoadFailed';
import { IconEdit, IconTrash, IconPlus, IconUpload, IconSparkles } from '@tabler/icons-react';
import { ExportButton } from '../../../components/ui/ExportButton';
import { ImportModal } from '../../../components/ui/ImportModal';
import { MapDraftModal } from '../../../components/competency/MapDraftModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { modals } from '@mantine/modals';
import { api } from '../../../utils/api';
import { showToast } from '../../../utils/toast';
import { getErrorMessage } from '../../../utils/api';
import { CompetencyMapModal } from '../../../components/competency/CompetencyMapModal';
import { CompetencyMap as CompetencyMapComponent } from '../../../components/competency/CompetencyMap';
import { CompetencyCreateModal } from '../../../components/competency/CompetencyCreateModal';
import { CompetencyMap, CompetencyMapData, CardSize, ViewMode, User } from '../../../types';
import { useCompetencyMap } from '../../../hooks/useApi';
import { CardSizeControl } from '../../../components/ui/CardSizeControl';
import { useDevice } from '../../../hooks/useDevice';
import { useAnalytics, AnalyticsEvents } from '../../../analytics';
import { CompetencyMapCards } from '../../../components/competency/CompetencyMapCards';
import { useHasFeature } from '../../../store/featureFlagsStore';
import { usePermission } from '../../../hooks/usePermission';

const CompetencyRoadmap = React.lazy(() =>
  import('../../../components/competency/roadmap').then((m) => ({ default: m.CompetencyRoadmap })),
);

// Тип для сырых данных карты компетенций из API
interface RawCompetencyMap {
  competencies?: Array<{
    competency: {
      _id: string;
      title: string;
      description?: string;
      tags?: string[];
      mentors?: unknown[];
      dependencies?: string[];
    };
    gradeId: string;
    swimlaneId: string;
    order: number;
    isRequired?: boolean;
  }>;
  grades: Array<{
    _id: string;
    title: string;
    description: string;
    qualification: string;
    position: string;
  }>;
  swimlanes: Array<{ _id: string; title: string }>;
}

// Функция для преобразования данных карты компетенций в нужный формат
const transformCompetencyMapData = (map: RawCompetencyMap): CompetencyMapData => {
  if (!map || !map.competencies) {
    return { grades: [], swimlanes: [] };
  }

  // Группируем компетенции по грейдам
  const competenciesByGrade = new Map();

  map.competencies.forEach((comp) => {
    const gradeId = comp.gradeId;
    if (!competenciesByGrade.has(gradeId)) {
      competenciesByGrade.set(gradeId, []);
    }

    // Преобразуем компетенцию в нужный формат
    const competency = {
      _id: comp.competency._id,
      title: comp.competency.title,
      description: comp.competency.description,
      tags: comp.competency.tags,
      swimlaneId: comp.swimlaneId,
      sortOrder: comp.order,
      mentors: comp.competency.mentors,
      isRequired: comp.isRequired || false,
      dependencies: comp.competency.dependencies || [],
    };

    competenciesByGrade.get(gradeId).push(competency);
  });

  // Создаем массив грейдов с компетенциями
  const grades = map.grades.map((grade) => ({
    _id: grade._id,
    title: grade.title,
    description: grade.description,
    qualification: grade.qualification,
    position: grade.position,
    competencies: competenciesByGrade.get(grade._id) || [],
  }));

  return {
    grades,
    swimlanes: map.swimlanes,
  };
};

export const CompetencyMapDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Страница доступна на чтение любому; редактирование — только админу.
  // Править карту и вернуться к списку карт — право maps.manage.
  const isAdmin = usePermission('maps.manage');
  const isMobile = useDevice() === 'mobile';
  const isCompact = useMediaQuery(`(max-width: ${em(1300)})`);
  const queryClient = useQueryClient();
  // Свои компетенции — чтобы полученные не показывались «не получена». Кэш общий с профилем.
  const { data: me } = useQuery<User>({
    queryKey: ['user', 'me'],
    queryFn: () => api('/users/me'),
  });
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMap, setEditMap] = useState<CompetencyMap | null>(null);
  const [showAddCompetencyModal, setShowAddCompetencyModal] = useState(false);
  const [cardSize, setCardSize] = useState<CardSize>('L');
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const isCardsOn = useHasFeature('mapCardsView');
  const isDraftOn = useHasFeature('mapAiDraft');
  const [isDraftOpen, setIsDraftOpen] = useState(false);
  const { track } = useAnalytics();
  const isTrackedRef = useRef(false);

  // Получение карты компетенций по ID
  const { data: map, isLoading, error } = useCompetencyMap(id || '');

  // Track page view
  useEffect(() => {
    if (map && !isTrackedRef.current) {
      isTrackedRef.current = true;
      track(AnalyticsEvents.COMPETENCY_MAP_VIEWED, { mapId: id, viewMode });
    }
  }, [map, id, viewMode, track]);

  // Мутация удаления
  const deleteMapMutation = useMutation({
    mutationFn: async (id: string) => api(`/competency-maps/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      showToast({ title: t('messages.deleted', { item: t('competencyMaps.title') }), message: '' });
      navigate('/competency-maps');
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorDeleting', { item: t('competencyMaps.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleEdit = () => {
    if (map) {
      setEditMap(map);
      setModalOpen(true);
    }
  };

  const handleDelete = () => {
    if (!map) return;

    modals.openConfirmModal({
      title: t('messages.confirmDelete', { item: map.name }),
      centered: true,
      children: <Text size="sm">{t('messages.confirmDelete', { item: map.name })}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        deleteMapMutation.mutate(map._id);
      },
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </PageContainer>
    );
  }

  if (error || !map) {
    return (
      <PageContainer>
        <LoadFailed onBack={() => navigate(isAdmin ? '/competency-maps' : '/grades')} hasRetry />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Group mb={{ base: 'md', sm: 'xl' }}>
        <Group wrap="nowrap" style={{ width: '100%' }}>
          {/* Non-admin не имеет доступа к списку /competency-maps; ведём на /grades —
              детерминированный дом (своя карта класса), без navigate(-1),
              который тупикует при прямом заходе / открытии в новой вкладке. */}
          <BackButton onClick={() => navigate(isAdmin ? '/competency-maps' : '/grades')} />
          <Title
            order={2}
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'nowrap' : undefined,
              display: isMobile ? '-webkit-box' : undefined,
              WebkitLineClamp: isMobile ? 1 : undefined,
              WebkitBoxOrient: isMobile ? 'vertical' : undefined,
            }}
          >
            {map.name}
          </Title>
          {/* Мутации доступны только админу. Рендерим условно (не display:none),
              чтобы обработчики и кнопки не оставались в DOM для non-admin. */}
          {isAdmin && (
            <Group gap="xs" ml="auto" wrap="nowrap">
              {isMobile ? (
                <ActionIcon
                  variant="light"
                  onClick={() => setShowAddCompetencyModal(true)}
                  size="lg"
                >
                  <IconPlus size={16} />
                </ActionIcon>
              ) : (
                <Button
                  leftSection={<IconPlus size={16} />}
                  variant="light"
                  onClick={() => setShowAddCompetencyModal(true)}
                >
                  {isCompact ? t('common.add') : t('competencyMaps.addCompetency')}
                </Button>
              )}
              {!isMobile && isDraftOn && (
                <Button
                  variant="light"
                  leftSection={<IconSparkles size={16} />}
                  onClick={() => setIsDraftOpen(true)}
                >
                  {t('competencyMaps.draft.open')}
                </Button>
              )}
              {!isMobile && <ExportButton url={`/competency-maps/${id}/export`} />}
              {!isMobile && (
                <Button
                  variant="light"
                  leftSection={<IconUpload size={16} />}
                  onClick={() => setIsImportOpen(true)}
                >
                  {t('importExport.import')}
                </Button>
              )}
              {isMobile ? (
                <ActionIcon variant="light" onClick={handleEdit} size="lg">
                  <IconEdit size={16} />
                </ActionIcon>
              ) : (
                <Button variant="light" leftSection={<IconEdit size={16} />} onClick={handleEdit}>
                  {isCompact ? t('common.edit') : t('competencyMaps.editMap')}
                </Button>
              )}
              {isMobile ? (
                <ActionIcon variant="light" color="red" onClick={handleDelete} size="lg">
                  <IconTrash size={16} />
                </ActionIcon>
              ) : (
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={handleDelete}
                >
                  {isCompact ? t('common.delete') : t('competencyMaps.deleteMap')}
                </Button>
              )}
            </Group>
          )}
        </Group>
        <Group
          justify="space-between"
          align="center"
          style={{ width: '100%' }}
          // На телефоне переключатель вида уходит под описание карты, а не сжимает его.
          wrap="wrap"
        >
          <div>
            <Text size="sm" c="dimmed">
              {(map.professions || []).map((p) => p.name).join(', ')}
            </Text>
            {map.grades && map.grades.length > 0 && (
              <Text size="xs" c="dimmed">
                {t('competencyMaps.grades')}: {map.grades.map((grade) => grade.title).join(', ')}
              </Text>
            )}
            {map.swimlanes && map.swimlanes.length > 0 && (
              <Text size="xs" c="dimmed">
                {t('competencyMaps.swimlanes')}:{' '}
                {map.swimlanes.map((swimlane) => swimlane.title).join(', ')}
              </Text>
            )}
          </div>
          <Group gap="sm" wrap="nowrap">
            <SegmentedControl
              value={viewMode}
              onChange={(value) => {
                track(AnalyticsEvents.COMPETENCY_MAP_VIEW_CHANGED, {
                  mapId: id,
                  from: viewMode,
                  to: value,
                });
                setViewMode(value as ViewMode);
              }}
              size="sm"
              data={[
                { value: 'map', label: t('competencyMaps.viewTable') },
                { value: 'roadmap', label: t('competencyMaps.viewRoadmap') },
                ...(isCardsOn ? [{ value: 'cards', label: t('competencyMaps.viewCards') }] : []),
              ]}
            />
            <CardSizeControl
              value={cardSize}
              onChange={setCardSize}
              style={isMobile ? { flexShrink: 0 } : undefined}
            />
          </Group>
        </Group>
      </Group>

      {/* Используем компонент CompetencyMap для отображения карты */}
      {viewMode === 'cards' && isCardsOn ? (
        <CompetencyMapCards
          data={transformCompetencyMapData(map)}
          competencyMapId={id}
          cardSize={cardSize}
          userCompetencies={me?.competencies}
          currentUser={me}
        />
      ) : viewMode === 'roadmap' ? (
        <React.Suspense fallback={<Loader />}>
          <CompetencyRoadmap
            competencyMapData={map ? transformCompetencyMapData(map) : undefined}
            competencyMapId={id}
            userCompetencies={me?.competencies}
            currentUser={me}
            // Как и в таблице: кольцо прогресса на общей карте — не про смотрящего.
            hideGradeProgress
          />
        </React.Suspense>
      ) : (
        <CompetencyMapComponent
          isDragDropEnabled={isAdmin}
          cardSize={cardSize}
          competencyMapData={map ? transformCompetencyMapData(map) : undefined}
          competencyMapId={id}
          userCompetencies={me?.competencies}
          currentUser={me}
          hideGradeProgress={true}
          // The map view offers the same self-service actions as the per-grade
          // view: the drawer lets the logged-in user submit a competency for
          // review (subject to the drawer's own state machine). grant/revoke stay
          // admin/team-lead only.
          hideStatusAndActions={false}
        />
      )}

      <CompetencyMapModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        editMap={editMap}
      />

      {/* Данные для селектов из объекта карты компетенций */}
      <CompetencyCreateModal
        opened={showAddCompetencyModal}
        onClose={() => setShowAddCompetencyModal(false)}
        swimlanes={map.swimlanes || []}
        grades={map.grades || []}
        competencyMapId={id}
      />

      {isDraftOn && (
        <MapDraftModal
          opened={isDraftOpen}
          onClose={() => setIsDraftOpen(false)}
          mapId={id || ''}
          grades={map.grades || []}
          swimlanes={map.swimlanes || []}
        />
      )}

      <ImportModal
        opened={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        importUrl={`/competency-maps/${id}/import`}
        templateUrl={`/competency-maps/${id}/import/template`}
        title={t('importExport.import')}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['competency-maps', id] })}
      />
    </PageContainer>
  );
};
